import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { toast } from "react-toastify";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import {
  getInvoiceOptions,
  searchMoloniCustomers,
  listInvoiceableProducts,
  createInvoice,
  getMoloniInvoice,
  updateMoloniInvoice,
  ensureFinalConsumer,
  createMoloniProduct,
  getProductCreationOptions,
  searchTpParts,
  syncTpPartToMoloni,
  refreshDocumentSetsAtStatus,
  refreshMoloniTaxes,
  getCompanyTaxes,
  getExemptionReasons,
  getMoloniCustomerDetails,
  lookupCustomerByVat,
  createMoloniCustomer,
  lookupVatInfo,
} from "../../services/invoicingServices";

// Fallback caso o backend ainda não tenha m_document_type sincronizado.
// IDs Moloni VARIAM por empresa — em produção usamos sempre o que vem
// em `options.invoice_document_types` (lido de m_document_type por empresa).
const INVOICE_TYPE_FALLBACK = [
  { id: 11, label: "Fatura (FT)", short: "FT" },
];

const NovaFactura = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { documentId } = useParams();
  const isEdit = Boolean(documentId);

  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [atRefreshing, setAtRefreshing] = useState(false);

  // Form state
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    customer: null,
    document_type_id: 11, // FT default
    document_set_id: null,
    date: today,
    expiration_date: today,
    payment_method_id: null,
    maturity_date_id: null,
    salesman_id: null,
    language_id: null,
    our_reference: "",
    your_reference: "",
    notes: "",
  });

  // Lines — cada linha:
  //   product_id, name, qty, price (sem IVA),
  //   iva_tax_id (Moloni tax id) | null,
  //   exemption_reason (M01..M99) | null  — ou um ou outro,
  //   extra_taxes: [{tax_id, value}] taxas opcionais não-IVA (Ecotaxa, IS).
  const newLine = () => ({
    product_id: null,
    name: "",
    qty: 1,
    price: 0,
    iva_tax_id: null,
    exemption_reason: null,
    extra_taxes: [],
  });
  const [lines, setLines] = useState([newLine()]);

  // Taxes da empresa (carregadas no mount)
  const [companyTaxes, setCompanyTaxes] = useState({ iva: [], other: [] });
  const [exemptionReasons, setExemptionReasons] = useState([]);

  // (Search de cliente foi movido para o modal — `customerModalSearch`)


  // Products (todos os m_product — cacheados na primeira search)
  const [products, setProducts] = useState([]);
  const [productsLoaded, setProductsLoaded] = useState(false);

  // Final Consumer quick-pick
  const [loadingFinalConsumer, setLoadingFinalConsumer] = useState(false);

  // Modal criar produto Moloni ad-hoc
  const [moloniProductModal, setMoloniProductModal] = useState({
    open: false,
    lineIdx: null,
  });
  const [moloniProductOptions, setMoloniProductOptions] = useState(null);
  const [moloniProductForm, setMoloniProductForm] = useState({
    name: "",
    reference: "",
    price: 0,
    category_id: null,
    unit_id: null,
    type: 1, // 1 produto, 2 serviço
    summary: "",
    taxes: [], // [{tax_id, value}] — só IVA (NOR/INT/RED) válido
    exemption_reason: null, // alternativa a taxes[]
  });
  const [moloniProductSaving, setMoloniProductSaving] = useState(false);

  const [showOptionalFields, setShowOptionalFields] = useState(false);

  // Modal criação cliente
  const [createCustomerModal, setCreateCustomerModal] = useState(false);
  const [createCustomerForm, setCreateCustomerForm] = useState({
    name: "",
    vat: "",
    email: "",
    phone: "",
    address: "",
    zip_code: "",
    city: "",
    country_id: 1, // PT
    notes: "",
  });
  const [createCustomerSaving, setCreateCustomerSaving] = useState(false);
  const [vatLookup, setVatLookup] = useState({
    checking: false,
    exists: false,
    customer: null,
  });
  const [vatSense, setVatSense] = useState({
    fetching: false,
    fetched: false,
    valid: false,
    name: "",
    address: "",
    error: null,
  });
  const openCreateCustomerModal = () => {
    setCreateCustomerForm({
      name: "",
      vat: "",
      email: "",
      phone: "",
      address: "",
      zip_code: "",
      city: "",
      country_id: 1,
      notes: "",
    });
    setVatLookup({ checking: false, exists: false, customer: null });
    setCreateCustomerModal(true);
  };
  const closeCreateCustomerModal = () => {
    if (createCustomerSaving) return;
    setCreateCustomerModal(false);
  };

  // Modal escolha cliente (substitui react-select)
  const [customerModal, setCustomerModal] = useState(false);
  const [customerModalSearch, setCustomerModalSearch] = useState("");
  const [customerModalResults, setCustomerModalResults] = useState([]);
  const [customerModalSearching, setCustomerModalSearching] = useState(false);
  const openCustomerModal = () => {
    setCustomerModal(true);
    setCustomerModalSearch("");
    setCustomerModalResults([]);
  };
  const closeCustomerModal = () => setCustomerModal(false);

  // Modal escolha produto (substitui react-select por linha)
  const [productModal, setProductModal] = useState({ open: false, lineIdx: null });
  const [productModalSearch, setProductModalSearch] = useState("");
  const openProductModal = (lineIdx) => {
    setProductModal({ open: true, lineIdx });
    setProductModalSearch("");
    ensureProductsLoaded();
  };
  const closeProductModal = () => setProductModal({ open: false, lineIdx: null });

  // Modal escolha taxas extra
  const [extraTaxModal, setExtraTaxModal] = useState({ open: false, lineIdx: null });
  const openExtraTaxModal = (lineIdx) => setExtraTaxModal({ open: true, lineIdx });
  const closeExtraTaxModal = () => setExtraTaxModal({ open: false, lineIdx: null });

  // Modal de motivo isenção
  const [exemptionModal, setExemptionModal] = useState({ open: false, lineIdx: null });
  const openExemptionModal = (lineIdx) => setExemptionModal({ open: true, lineIdx });
  const closeExemptionModal = () => setExemptionModal({ open: false, lineIdx: null });
  const applyExemption = (code) => {
    const idx = exemptionModal.lineIdx;
    if (idx == null) return;
    updateLine(idx, { iva_tax_id: null, exemption_reason: code });
    closeExemptionModal();
  };

  // Flow B — sync peça TP para Moloni e aplicar na linha
  const [tpSyncModal, setTpSyncModal] = useState({
    open: false,
    lineIdx: null,
  });
  const [tpSearch, setTpSearch] = useState("");
  const [tpResults, setTpResults] = useState([]);
  const [tpSearching, setTpSearching] = useState(false);
  const [tpSelectedPart, setTpSelectedPart] = useState(null);
  const [tpPriceOverride, setTpPriceOverride] = useState("");
  const [tpQty, setTpQty] = useState(1);
  const [tpSyncing, setTpSyncing] = useState(false);

  // Refresh do cache AT em background. Demora ~30s pelo rate-limit
  // Moloni mas é silencioso — quando termina, recarrega `options`
  // para mostrar os badges no dropdown de série.
  const refreshAtCache = async () => {
    try {
      setAtRefreshing(true);
      await refreshDocumentSetsAtStatus();
      const fresh = await getInvoiceOptions();
      setOptions(fresh?.data?.data || null);
    } catch (_) {
      // silencioso — UI continua usável sem badge
    } finally {
      setAtRefreshing(false);
    }
  };

  // (loadTaxesAndReasons foi inlined no useEffect para podermos usar os
  // dados imediatamente no prefill do edit antes do state setter
  // propagar.)
  const refreshTaxesBg = async () => {
    try {
      await refreshMoloniTaxes();
      const tx = await getCompanyTaxes();
      setCompanyTaxes(tx?.data?.data || { iva: [], other: [] });
    } catch (_) {}
  };

  useEffect(() => {
    (async () => {
      try {
        const resp = await getInvoiceOptions();
        const data = resp?.data?.data || null;
        // Empresa sem ligação a software de facturação activa →
        // redirecciona para a lista, que mostra o alerta dedicado e
        // o link para Definições.
        const hasProvider =
          resp?.data?.status === 200 &&
          data &&
          (data.document_sets ||
            data.payment_methods ||
            data.invoice_document_types);
        if (!hasProvider) {
          toast.warning(
            "Sem ligação a software de facturação activa. Configura nas Definições.",
          );
          navigate("/faturacao");
          return;
        }
        setOptions(data);

        // Aplica defaults da empresa Moloni (payment, maturity, language)
        // — só se o user ainda não escolheu nada.
        const cd = data?.company_defaults || {};
        if (cd && (cd.payment_method_id || cd.maturity_date_id || cd.language_id)) {
          setForm((p) => ({
            ...p,
            payment_method_id: p.payment_method_id || cd.payment_method_id || null,
            maturity_date_id: p.maturity_date_id || cd.maturity_date_id || null,
            language_id: p.language_id || cd.language_id || null,
          }));
        }

        // Se algum set não tem at_communicated cacheado (null), dispara
        // refresh em background. Não bloqueia o load.
        const sets = data?.document_sets || [];
        const hasNullAt = sets.some((s) => s.at_communicated === null);
        if (hasNullAt) {
          // fire-and-forget — não await
          refreshAtCache();
        }

        // Carrega taxas + motivos isenção; refresca m_tax em background.
        // Guardamos os IDs IVA num Set para usar imediatamente no prefill
        // do edit (state setter pode não ter ainda actualizado).
        const taxesResp = await getCompanyTaxes();
        const taxesData = taxesResp?.data?.data || { iva: [], other: [] };
        setCompanyTaxes(taxesData);
        const exsResp = await getExemptionReasons();
        setExemptionReasons(exsResp?.data?.data || []);
        refreshTaxesBg();

        const ivaIdsSet = new Set(
          (taxesData.iva || []).map((t) => Number(t.tax_id)),
        );

        if (isEdit) {
          // Load existing invoice
          const detailResp = await getMoloniInvoice(documentId);
          const d = detailResp?.data?.data || {};
          if (Number(d.status) === 1) {
            toast.error(
              t("cannot_edit_final", {
                defaultValue: "Facturas finais não podem ser editadas.",
              }),
            );
            navigate("/faturacao");
            return;
          }
          setForm({
            customer: {
              customer_id: d.customer_id,
              name: d.entity_name || "",
              vat: d.entity_vat || "",
              email: "",
            },
            document_type_id: d.document_type_id || 11,
            document_set_id: d.document_set_id || null,
            date: (d.date || "").slice(0, 10) || new Date().toISOString().slice(0, 10),
            expiration_date:
              (d.expiration_date || "").slice(0, 10) ||
              new Date().toISOString().slice(0, 10),
            payment_method_id: d.payment_method_id || null,
            maturity_date_id: d.maturity_date_id || null,
            salesman_id: d.salesman_id || null,
            language_id: d.language_id || null,
            our_reference: d.our_reference || "",
            your_reference: d.your_reference || "",
            notes: d.notes || "",
          });
          setLines(
            (d.products || []).map((p) => {
              // Classifica cada tax: IVA real ou extra
              const taxesArr = Array.isArray(p.taxes) ? p.taxes : [];
              let ivaTaxId = null;
              const extraTaxes = [];
              for (const t of taxesArr) {
                const tid = Number(t.tax_id);
                if (!tid) continue;
                if (ivaIdsSet.has(tid) && ivaTaxId == null) {
                  // Primeira IVA encontrada vira a IVA da linha
                  ivaTaxId = tid;
                } else {
                  extraTaxes.push({
                    tax_id: tid,
                    value: Number(t.value) || 0,
                  });
                }
              }
              return {
                product_id: p.product_id,
                name: p.name || "",
                qty: Number(p.qty) || 1,
                price: Number(p.price) || 0,
                iva_tax_id: ivaTaxId,
                exemption_reason: (p.exemption_reason || "").trim() || null,
                extra_taxes: extraTaxes,
              };
            }),
          );
        } else {
          // Default: tenta usar o FT real da empresa (m_document_type)
          // antes de cair no 11 hardcoded (que para algumas empresas
          // Moloni nem é Fatura).
          const types = resp?.data?.data?.invoice_document_types || [];
          const ftRow = types.find((t) => t.short === "FT") || types[0];
          const defaults = resp?.data?.data?.default_sets_by_type || {};
          const chosenTypeId = ftRow
            ? Number(ftRow.document_type_id)
            : form.document_type_id;
          const setId = defaults[String(chosenTypeId)]
            ? Number(defaults[String(chosenTypeId)])
            : null;
          setForm((prev) => ({
            ...prev,
            document_type_id: chosenTypeId,
            ...(setId ? { document_set_id: setId } : {}),
          }));
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || err.message);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carrega produtos na primeira abertura do dropdown
  const ensureProductsLoaded = async () => {
    if (productsLoaded) return;
    try {
      const resp = await listInvoiceableProducts();
      const rows = resp?.data?.data || [];
      setProducts(rows);
      setProductsLoaded(true);
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    }
  };

  // Quando o user escolhe (ou re-escolhe) o cliente, vamos buscar a
  // ficha Moloni e auto-popular: série default (se user ainda não
  // escolheu), payment/maturity, e default IVA por linha.
  const applyCustomerDefaults = async (customer) => {
    if (!customer || !customer.customer_id) return;
    try {
      const r = await getMoloniCustomerDetails(customer.customer_id);
      const c = r?.data?.data;
      if (!c) return;
      // Override: campo ainda igual ao default da empresa OU nulo → usa
      // o do cliente. Se user já mudou manualmente, preserva.
      const cd = options?.company_defaults || {};
      const orCustomer = (key) => (val) =>
        val == null || (cd[key] != null && Number(val) === Number(cd[key]))
          ? c[key] || val
          : val;

      setForm((prev) => ({
        ...prev,
        customer: { ...prev.customer, ...c },
        document_set_id: orCustomer("document_set_id")(prev.document_set_id),
        payment_method_id: orCustomer("payment_method_id")(prev.payment_method_id),
        maturity_date_id: orCustomer("maturity_date_id")(prev.maturity_date_id),
        salesman_id: prev.salesman_id || c.salesman_id || null,
        language_id: orCustomer("language_id")(prev.language_id),
      }));
      // Auto-fill IVA / isenção nas linhas que ainda não têm.
      // Preferência:
      //   1. Cliente tem tax_id default → usa esse.
      //   2. Senão, usa IVA NOR da empresa (active_by_default).
      const customerTax = c.tax_id;
      const defaultIva = (companyTaxes.iva || []).find((t) => t.is_default) ||
        (companyTaxes.iva || [])[0];
      const ivaToApply = customerTax || (defaultIva && defaultIva.tax_id);
      if (ivaToApply) {
        setLines((prev) => prev.map((l) => {
          if (l.iva_tax_id || l.exemption_reason) return l;
          return { ...l, iva_tax_id: Number(ivaToApply), exemption_reason: null };
        }));
      } else if (c.exemption_reason) {
        setLines((prev) => prev.map((l) => {
          if (l.iva_tax_id || l.exemption_reason) return l;
          return { ...l, iva_tax_id: null, exemption_reason: c.exemption_reason };
        }));
      }
    } catch (_) {}
  };

  const pickFinalConsumer = async () => {
    try {
      setLoadingFinalConsumer(true);
      const resp = await ensureFinalConsumer();
      const c = resp?.data?.data?.customer;
      if (c) {
        setForm((p) => ({ ...p, customer: c }));
        applyCustomerDefaults(c);
        toast.success(
          resp?.data?.data?.created
            ? t("final_consumer_created", {
                defaultValue: "Consumidor Final criado na Moloni.",
              })
            : t("final_consumer_selected", {
                defaultValue: "Consumidor Final seleccionado.",
              }),
        );
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setLoadingFinalConsumer(false);
    }
  };

  const openMoloniProductModal = async (lineIdx) => {
    setMoloniProductModal({ open: true, lineIdx });
    if (!moloniProductOptions) {
      try {
        const resp = await getProductCreationOptions();
        const data = resp?.data?.data || null;
        setMoloniProductOptions(data);
        // Preselect active_by_default taxa (primeiro com flag)
        const defaultTax = (data?.taxes || []).find((tx) => tx.active_by_default);
        if (defaultTax) {
          setMoloniProductForm((p) => ({
            ...p,
            taxes: [
              {
                tax_id: defaultTax.tax_id,
                value: defaultTax.value,
              },
            ],
          }));
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || err.message);
      }
    }
  };

  const closeMoloniProductModal = () => {
    if (moloniProductSaving) return;
    setMoloniProductModal({ open: false, lineIdx: null });
  };

  const saveMoloniProduct = async () => {
    const f = moloniProductForm;
    if (!f.name || !f.reference || !f.category_id || !f.unit_id) {
      toast.error(
        t("product_fields_required", {
          defaultValue: "Nome, referência, categoria e unidade obrigatórios.",
        }),
      );
      return;
    }
    if (
      (!Array.isArray(f.taxes) || f.taxes.length === 0) &&
      !f.exemption_reason
    ) {
      toast.error(
        "Tens de escolher uma taxa IVA ou um motivo de isenção.",
      );
      return;
    }
    try {
      setMoloniProductSaving(true);
      const resp = await createMoloniProduct({
        name: f.name,
        reference: f.reference,
        price: Number(f.price),
        category_id: Number(f.category_id),
        unit_id: Number(f.unit_id),
        type: Number(f.type),
        summary: f.summary,
        taxes: f.taxes,
        exemption_reason: f.exemption_reason || undefined,
      });
      const p = resp?.data?.data?.product;
      if (!p) throw new Error("Resposta sem produto");
      // Força recarregar a lista de produtos no próximo "Procurar"
      setProductsLoaded(false);
      setProducts([]);
      // Mete na linha aberta. Aplica o IVA escolhido no modal; se não
      // foi escolhido (caso por algum motivo passou validação), usa o
      // mesmo cascata do auto-fill normal: tax_id do cliente → default
      // IVA da empresa → primeira IVA da lista.
      const idx = moloniProductModal.lineIdx;
      if (idx !== null && idx !== undefined) {
        let lineIva = null;
        let lineExempt = null;
        if (Array.isArray(f.taxes) && f.taxes[0]?.tax_id) {
          lineIva = Number(f.taxes[0].tax_id);
        } else if (f.exemption_reason) {
          lineExempt = String(f.exemption_reason);
        } else {
          const customerTax = form.customer?.tax_id;
          const defaultIva =
            (companyTaxes.iva || []).find((tt) => tt.is_default) ||
            (companyTaxes.iva || [])[0];
          if (customerTax) lineIva = Number(customerTax);
          else if (defaultIva) lineIva = Number(defaultIva.tax_id);
        }
        updateLine(idx, {
          product_id: p.product_id,
          name: p.name,
          price: p.price,
          iva_tax_id: lineIva,
          exemption_reason: lineExempt,
        });
      }
      toast.success(
        t("product_created", { defaultValue: "Produto criado na Moloni." }),
      );
      setMoloniProductModal({ open: false, lineIdx: null });
      setMoloniProductForm({
        name: "",
        reference: "",
        price: 0,
        category_id: null,
        unit_id: null,
        type: 1,
        summary: "",
        taxes: moloniProductForm.taxes,
      });
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setMoloniProductSaving(false);
    }
  };

  const openTpSyncModal = (lineIdx) => {
    setTpSyncModal({ open: true, lineIdx });
    setTpSearch("");
    setTpResults([]);
    setTpSelectedPart(null);
    setTpPriceOverride("");
    setTpQty(1);
  };
  const closeTpSyncModal = () => {
    if (tpSyncing) return;
    setTpSyncModal({ open: false, lineIdx: null });
  };

  // Debounce search TP parts
  useEffect(() => {
    if (!tpSyncModal.open) return;
    if (!tpSearch || tpSearch.length < 2) {
      setTpResults([]);
      return;
    }
    const id = setTimeout(async () => {
      try {
        setTpSearching(true);
        const resp = await searchTpParts(tpSearch, 30);
        setTpResults(resp?.data?.data || []);
      } catch (_) {
        setTpResults([]);
      } finally {
        setTpSearching(false);
      }
    }, 400);
    return () => clearTimeout(id);
  }, [tpSearch, tpSyncModal.open]);

  const runTpSync = async () => {
    if (!tpSelectedPart) return;
    try {
      setTpSyncing(true);
      const resp = await syncTpPartToMoloni(tpSelectedPart.id, {
        priceOverride:
          tpPriceOverride !== "" && !isNaN(Number(tpPriceOverride))
            ? Number(tpPriceOverride)
            : undefined,
      });
      const data = resp?.data?.data || {};
      const moloniProducts = data.products || [];
      if (moloniProducts.length === 0) {
        toast.error(
          t("sync_no_products", {
            defaultValue: "Sync sem resultado — verifica quantidade/preço.",
          }),
        );
        return;
      }

      const wantQty = Math.max(1, Number(tpQty) || 1);
      const finalPrice =
        tpPriceOverride !== "" && !isNaN(Number(tpPriceOverride))
          ? Number(tpPriceOverride)
          : Number(moloniProducts[0].price);

      // Cada produto Moloni representa uma unidade. Vamos criar tantas
      // linhas de factura quantas pedidas (1 a N), uma por unidade.
      const idx = tpSyncModal.lineIdx;
      const productsToAdd = moloniProducts.slice(0, wantQty);

      if (idx !== null && idx !== undefined && productsToAdd.length > 0) {
        // Linha actual usa o primeiro produto sincronizado
        updateLine(idx, {
          product_id: productsToAdd[0].moloni_id,
          name: productsToAdd[0].name,
          price: finalPrice,
        });
        // Linhas adicionais para os restantes
        if (productsToAdd.length > 1) {
          setLines((prev) => {
            const extras = productsToAdd.slice(1).map((p) => ({
              ...newLine(),
              product_id: p.moloni_id,
              name: p.name,
              price: finalPrice,
              iva_tax_id: prev[idx]?.iva_tax_id || null,
              exemption_reason: prev[idx]?.exemption_reason || null,
            }));
            const next = [...prev];
            next.splice(idx + 1, 0, ...extras);
            return next;
          });
        }
      }
      // Força re-fetch da lista de produtos para incluir os novos
      setProductsLoaded(false);
      setProducts([]);
      toast.success(
        productsToAdd.length > 1
          ? `${productsToAdd.length} unidades sincronizadas e adicionadas à factura.`
          : data.message ||
              t("sync_ok", { defaultValue: "Peça sincronizada." }),
      );
      closeTpSyncModal();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setTpSyncing(false);
    }
  };

  // Debounce: dedup local Moloni + VATSense lookup. Ambos correm quando
  // o NIF tem ≥ 8 caracteres (PT NIF=9; UE varia 8-12). VATSense valida
  // contra VIES e devolve nome+morada da empresa.
  useEffect(() => {
    if (!createCustomerModal) return;
    const v = String(createCustomerForm.vat || "").trim();
    if (!v || v.length < 8) {
      setVatLookup({ checking: false, exists: false, customer: null });
      setVatSense({
        fetching: false,
        fetched: false,
        valid: false,
        name: "",
        address: "",
        error: null,
      });
      return;
    }
    setVatLookup((p) => ({ ...p, checking: true }));
    setVatSense((p) => ({ ...p, fetching: true, fetched: false, error: null }));
    const id = setTimeout(async () => {
      // 1) Dedup interno Moloni
      try {
        const r = await lookupCustomerByVat(v);
        const data = r?.data?.data;
        setVatLookup({
          checking: false,
          exists: !!data?.exists,
          customer: data?.exists ? data.customer : null,
        });
      } catch (_) {
        setVatLookup({ checking: false, exists: false, customer: null });
      }

      // 2) VATSense (VIES) — só se ainda não foi auto-fill antes para
      //    este VAT específico
      try {
        const country =
          createCustomerForm.country_id === 67
            ? "ES"
            : createCustomerForm.country_id === 73
            ? "FR"
            : createCustomerForm.country_id === 80
            ? "DE"
            : createCustomerForm.country_id === 105
            ? "IT"
            : "PT";
        const r = await lookupVatInfo(v, country);
        const d = r?.data?.data;
        if (d?.valid && d?.company?.name) {
          setVatSense({
            fetching: false,
            fetched: true,
            valid: true,
            name: d.company.name || "",
            address: d.company.full_address || d.company.address || "",
            error: null,
          });
        } else {
          setVatSense({
            fetching: false,
            fetched: true,
            valid: false,
            name: "",
            address: "",
            error: r?.data?.data?.message || "VAT inválido ou sem dados",
          });
        }
      } catch (e) {
        setVatSense({
          fetching: false,
          fetched: true,
          valid: false,
          name: "",
          address: "",
          error: e?.response?.data?.message || e.message,
        });
      }
    }, 600);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createCustomerForm.vat, createCustomerModal]);

  // Resolve a IVA / isenção a aplicar a uma linha quando um produto é
  // escolhido. Cascata:
  //   1. Se o próprio produto tem IVA real (saft_type=1, NOR/INT/RED)
  //      nas suas taxes guardadas em m_product → usa.
  //   2. Senão, tax_id default do cliente seleccionado.
  //   3. Senão, IVA active_by_default da empresa.
  //   4. Senão, primeira IVA disponível.
  //   Se produto tem exemption_reason válido (vat_type=ISE), usa isso.
  const resolveDefaultIvaForLine = (productRow = null) => {
    const ivaIds = new Set(
      (companyTaxes.iva || []).map((t) => Number(t.tax_id)),
    );
    // 1. Tenta extrair IVA do próprio produto (m_product.taxes JSON)
    if (productRow && productRow.taxes) {
      try {
        const arr = typeof productRow.taxes === "string"
          ? JSON.parse(productRow.taxes)
          : productRow.taxes;
        if (Array.isArray(arr)) {
          for (const t of arr) {
            const tid = Number(t.tax_id);
            if (tid && ivaIds.has(tid)) {
              return { iva_tax_id: tid, exemption_reason: null };
            }
          }
        }
      } catch (_) {}
    }
    // Produto isento explicitamente (sem taxes IVA mas com exemption_reason)
    if (productRow && productRow.exemption_reason) {
      const ex = String(productRow.exemption_reason).trim();
      if (ex) return { iva_tax_id: null, exemption_reason: ex };
    }
    // 2. Default do cliente
    if (form.customer?.tax_id) {
      return { iva_tax_id: Number(form.customer.tax_id), exemption_reason: null };
    }
    // 3. IVA company default
    const def =
      (companyTaxes.iva || []).find((t) => t.is_default) ||
      (companyTaxes.iva || [])[0];
    if (def) {
      return { iva_tax_id: Number(def.tax_id), exemption_reason: null };
    }
    return { iva_tax_id: null, exemption_reason: null };
  };

  const applyVatSenseToForm = () => {
    if (!vatSense.valid) return;
    // VATSense (VIES) devolve morada multi-linha:
    //   "Rua X N 38\nBraga\n4715-163 Braga"
    // Parsing best-effort:
    //   - última linha com CP "1234-567 Cidade" → zip_code + city
    //   - primeira linha → address
    //   - resto → joined em address
    let address = "";
    let zip_code = "";
    let city = "";
    if (vatSense.address) {
      const lines = vatSense.address
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);
      if (lines.length > 0) {
        const last = lines[lines.length - 1];
        const m = last.match(/^(\d{4}(?:-\d{3,4})?)\s+(.+)$/);
        if (m) {
          zip_code = m[1];
          city = m[2];
          address = lines.slice(0, -1).join(", ");
        } else {
          city = last;
          address = lines.slice(0, -1).join(", ");
          if (!address) address = last;
        }
      }
    }
    setCreateCustomerForm((p) => ({
      ...p,
      name: vatSense.name || p.name,
      address: address || p.address,
      zip_code: zip_code || p.zip_code,
      city: city || p.city,
    }));
    toast.success("Dados VIES aplicados — verifica e ajusta se necessário.");
  };

  const useExistingCustomerFromLookup = () => {
    if (!vatLookup.customer) return;
    const c = vatLookup.customer;
    setForm((p) => ({ ...p, customer: c }));
    applyCustomerDefaults(c);
    setCreateCustomerModal(false);
    setCustomerModal(false);
    toast.info(`Cliente "${c.name}" já existia — seleccionado.`);
  };

  const submitCreateCustomer = async () => {
    const f = createCustomerForm;
    if (!f.name.trim()) {
      toast.error("Nome obrigatório");
      return;
    }
    if (!f.vat.trim()) {
      toast.error("NIF/VAT obrigatório");
      return;
    }
    if (vatLookup.exists && vatLookup.customer) {
      toast.warning(
        "Este NIF já existe. Usa o cliente existente em vez de criar duplicado.",
      );
      return;
    }
    try {
      setCreateCustomerSaving(true);
      const resp = await createMoloniCustomer({
        name: f.name.trim(),
        vat: f.vat.trim(),
        email: f.email.trim(),
        phone: f.phone.trim(),
        address: f.address.replace(/[\r\n]+/g, ", ").trim(),
        zip_code: f.zip_code.trim(),
        city: f.city.trim(),
        country_id: Number(f.country_id) || 1,
        notes: f.notes.trim(),
      });
      const data = resp?.data?.data;
      if (!data?.customer) throw new Error("Resposta inesperada");
      const c = data.customer;
      setForm((p) => ({ ...p, customer: c }));
      applyCustomerDefaults(c);
      setCreateCustomerModal(false);
      setCustomerModal(false);
      toast.success(
        data.existed
          ? `Cliente já existia na Moloni — seleccionado.`
          : `Cliente "${c.name}" criado e seleccionado.`,
      );
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setCreateCustomerSaving(false);
    }
  };

  // Debounce customer search (no modal)
  useEffect(() => {
    if (!customerModal) return;
    if (!customerModalSearch || customerModalSearch.length < 2) {
      setCustomerModalResults([]);
      return;
    }
    const id = setTimeout(async () => {
      try {
        setCustomerModalSearching(true);
        const resp = await searchMoloniCustomers(customerModalSearch, 30);
        setCustomerModalResults(resp?.data?.data || []);
      } catch (_) {
      } finally {
        setCustomerModalSearching(false);
      }
    }, 400);
    return () => clearTimeout(id);
  }, [customerModalSearch, customerModal]);

  // (productOptions e customerOptions removidos — selecção é via modal)

  // Quando muda o tipo de documento, auto-aplica default set
  const onChangeType = (typeId) => {
    const defaults = options?.default_sets_by_type || {};
    const defaultSetId = defaults[String(typeId)];
    setForm((prev) => ({
      ...prev,
      document_type_id: typeId,
      document_set_id: defaultSetId ? Number(defaultSetId) : prev.document_set_id,
    }));
  };

  const availableSetsForType = useMemo(() => {
    if (!options?.document_sets) return [];
    const typeId = Number(form.document_type_id);
    return options.document_sets.filter((s) =>
      (s.document_types || []).some(
        (t) => Number(t.document_type_id) === typeId
      )
    );
  }, [options, form.document_type_id]);

  // Tipos de documento Moloni reais (FT/FR/FS) — vêm da tabela
  // m_document_type sincronizada por empresa Moloni. NUNCA hardcoded:
  // os IDs Moloni variam por empresa (id 3 pode ser FR num cliente e
  // NC noutro), pelo que confiamos só no que veio do backend.
  const invoiceTypeOptions = useMemo(() => {
    const list = options?.invoice_document_types;
    if (Array.isArray(list) && list.length > 0) {
      return list.map((t) => ({ value: t.document_type_id, label: t.label }));
    }
    return INVOICE_TYPE_FALLBACK.map((t) => ({ value: t.id, label: t.label }));
  }, [options]);

  // (total bruto agora vem de `taxesBreakdown.baseTotal`)

  // Quadro de totais e impostos aplicados (agrupados por taxa/motivo).
  // Usa companyTaxes/exemptionReasons para obter nomes legíveis.
  const taxesBreakdown = useMemo(() => {
    const groups = new Map(); // key → { kind, label, base, vat, rate? }
    let baseTotal = 0;
    let vatTotal = 0;
    let extrasTotal = 0;

    for (const l of lines) {
      const sub = Number(l.qty || 0) * Number(l.price || 0);
      if (sub <= 0) continue;
      baseTotal += sub;

      // IVA ou Isenção
      if (l.iva_tax_id) {
        const t = (companyTaxes.iva || []).find(
          (x) => Number(x.tax_id) === Number(l.iva_tax_id),
        );
        const rate = t ? Number(t.value) : 0;
        const vat = sub * (rate / 100);
        vatTotal += vat;
        const key = `iva_${l.iva_tax_id}`;
        const existing = groups.get(key);
        if (existing) {
          existing.base += sub;
          existing.vat += vat;
        } else {
          groups.set(key, {
            kind: "iva",
            label: t ? `${t.name} — ${t.value}%` : `IVA #${l.iva_tax_id}`,
            rate,
            base: sub,
            vat,
          });
        }
      } else if (l.exemption_reason) {
        const r = exemptionReasons.find((e) => e.code === l.exemption_reason);
        const key = `exempt_${l.exemption_reason}`;
        const existing = groups.get(key);
        if (existing) existing.base += sub;
        else
          groups.set(key, {
            kind: "exempt",
            label: r
              ? `Isento IVA — ${r.code}: ${r.description}`
              : `Isento IVA — ${l.exemption_reason}`,
            rate: 0,
            base: sub,
            vat: 0,
          });
      }

      // Extras (Ecotaxa, IS, etc.)
      for (const et of l.extra_taxes || []) {
        const tx = (companyTaxes.other || []).find(
          (x) => Number(x.tax_id) === Number(et.tax_id),
        );
        if (!tx) continue;
        const isPercentage = Number(tx.type) === 1;
        const amt = isPercentage
          ? sub * (Number(tx.value) / 100)
          : Number(tx.value); // fixed value per linha
        extrasTotal += amt;
        const key = `extra_${et.tax_id}`;
        const existing = groups.get(key);
        if (existing) {
          existing.base += sub;
          existing.vat += amt;
        } else {
          groups.set(key, {
            kind: "extra",
            label: `${tx.name}${
              tx.value ? ` — ${tx.value}${isPercentage ? "%" : "€"}` : ""
            }`,
            rate: isPercentage ? Number(tx.value) : 0,
            base: sub,
            vat: amt,
          });
        }
      }
    }

    return {
      groups: Array.from(groups.values()),
      baseTotal,
      vatTotal,
      extrasTotal,
      grandTotal: baseTotal + vatTotal + extrasTotal,
    };
  }, [lines, companyTaxes, exemptionReasons]);

  const updateLine = (idx, patch) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };
  const addLine = () =>
    setLines((prev) => [...prev, { product_id: null, name: "", qty: 1, price: 0 }]);
  const removeLine = (idx) =>
    setLines((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  const buildPayload = () => ({
    customer_id: form.customer.customer_id,
    // Denorm para a mirror local (o backend guarda isto em m_invoice)
    entity_name: form.customer.name || undefined,
    entity_vat: form.customer.vat || undefined,
    entity_email: form.customer.email || undefined,
    document_set_id: form.document_set_id,
    document_type_id: form.document_type_id,
    date: form.date,
    expiration_date: form.expiration_date,
    our_reference: form.our_reference,
    your_reference: form.your_reference,
    notes: form.notes,
    payment_method_id: form.payment_method_id || undefined,
    maturity_date_id: form.maturity_date_id || undefined,
    salesman_id: form.salesman_id || undefined,
    language_id: form.language_id || undefined,
    products: lines.map((l) => {
      const taxes = [];
      if (l.iva_tax_id) {
        const t = (companyTaxes.iva || []).find(
          (x) => Number(x.tax_id) === Number(l.iva_tax_id),
        );
        taxes.push({
          tax_id: Number(l.iva_tax_id),
          value: Number(t?.value) || 0,
          order: 1,
          cumulative: 0,
        });
      }
      (l.extra_taxes || []).forEach((et, i) => {
        taxes.push({
          tax_id: Number(et.tax_id),
          value: Number(et.value) || 0,
          order: i + 2,
          cumulative: 0,
        });
      });
      return {
        product_id: l.product_id,
        name: l.name || undefined,
        qty: Number(l.qty),
        price: Number(l.price),
        taxes: taxes.length ? taxes : undefined,
        exemption_reason: l.exemption_reason || undefined,
      };
    }),
  });

  const validateForSubmit = () => {
    if (!form.customer) {
      toast.error(t("select_customer", { defaultValue: "Escolhe um cliente" }));
      return false;
    }
    if (!form.document_set_id) {
      toast.error(t("select_series", { defaultValue: "Escolhe uma série" }));
      return false;
    }
    if (!lines.length || lines.some((l) => !l.product_id || !l.qty || !l.price)) {
      toast.error(
        t("invalid_lines", {
          defaultValue: "Cada linha precisa de produto, quantidade e preço",
        }),
      );
      return false;
    }
    const noTaxLine = lines.find((l) => !l.iva_tax_id && !l.exemption_reason);
    if (noTaxLine) {
      toast.error(
        t("line_needs_tax", {
          defaultValue:
            "Cada linha precisa de uma taxa IVA OU motivo de isenção. Escolhe na coluna Imposto IVA.",
        }),
      );
      return false;
    }
    return true;
  };

  // submitMode: "draft" (status=0) | "final" (status=1 com try-fallback AT)
  const submit = async (submitMode = "draft") => {
    if (!validateForSubmit()) return;

    if (submitMode === "final") {
      const confirmed = window.confirm(
        t("confirm_final_emission", {
          defaultValue:
            "Vais emitir uma factura FINAL (documento contabilístico irreversível). Se a série ainda não estiver comunicada à AT, ficará como rascunho automaticamente. Continuar?",
        }),
      );
      if (!confirmed) return;
    }

    try {
      setSubmitting(true);
      const status = submitMode === "final" ? 1 : 0;
      const resp = isEdit
        ? await updateMoloniInvoice(documentId, buildPayload(), { status })
        : await createInvoice(buildPayload(), { status });
      const data = resp?.data?.data || {};
      const fallbackUsed = Boolean(data.fallback_used);
      const finalStatus = Number(data.status);

      if (finalStatus === 1) {
        toast.success(
          resp?.data?.message ||
            t("invoice_final_ok", { defaultValue: "Factura final emitida" }),
        );
      } else if (fallbackUsed) {
        // Cenário raro: série tem AT para alguns document_types mas não
        // para o tipo escolhido. A Moloni aceitou status=0 mesmo assim.
        toast.warning(
          resp?.data?.message ||
            t("invoice_partial_at", {
              defaultValue:
                "Tipo de documento sem comunicação AT nesta série — gravado como rascunho. Comunica este tipo no portal Moloni.",
            }),
          { autoClose: 10000 },
        );
      } else {
        toast.success(
          resp?.data?.message ||
            t("draft_created", { defaultValue: "Rascunho criado" }),
        );
      }
      navigate("/faturacao");
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div style={{ padding: 20 }}>A carregar...</div>;
  if (!options)
    return (
      <div className="alert alert-danger" style={{ margin: 20 }}>
        Não foi possível carregar opções. Verifica ligação Moloni em Settings.
      </div>
    );

  return (
    <div className="content-wrapper">
      <section className="content-header">
        <div
          className="d-flex justify-content-between align-items-center"
          style={{ padding: "10px 20px" }}
        >
          <h3 style={{ margin: 0 }}>
            {isEdit
              ? t("invoicing_edit_draft", {
                  defaultValue: "Editar rascunho",
                })
              : t("invoicing_new_draft", {
                  defaultValue: "Nova factura (rascunho)",
                })}
          </h3>
          <button
            type="button"
            className="btn btn-link"
            onClick={() => navigate("/faturacao")}
          >
            &larr; {t("back", { defaultValue: "Voltar" })}
          </button>
        </div>
      </section>

      <section className="content" style={{ padding: 20 }}>
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">
              {t("customer", { defaultValue: "Cliente" })} *
            </label>
            {form.customer ? (
              <div
                style={{
                  border: "1px solid #ced4da",
                  borderRadius: 6,
                  padding: 12,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 12,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>
                    {form.customer.name || "—"}
                  </div>
                  <div style={{ color: "#6c757d", fontSize: 13, marginTop: 2 }}>
                    {form.customer.vat && <>NIF {form.customer.vat}</>}
                    {form.customer.email && <> · {form.customer.email}</>}
                  </div>
                  {(form.customer.address || form.customer.city) && (
                    <div style={{ color: "#6c757d", fontSize: 12, marginTop: 2 }}>
                      {[form.customer.address, form.customer.zip_code, form.customer.city]
                        .filter(Boolean)
                        .join(", ")}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={openCustomerModal}
                  >
                    <i className="bi bi-pencil"></i> Trocar
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => setForm((p) => ({ ...p, customer: null }))}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={openCustomerModal}
                  style={{ flex: "1 1 200px" }}
                >
                  <i className="bi bi-search"></i> Procurar cliente
                </button>
                <button
                  type="button"
                  className="btn btn-outline-success"
                  onClick={openCreateCustomerModal}
                  title="Criar novo cliente na Moloni"
                >
                  <i className="bi bi-person-plus"></i> Novo
                </button>
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={pickFinalConsumer}
                  disabled={loadingFinalConsumer}
                  title="Consumidor Final (NIF 999999990)"
                >
                  {loadingFinalConsumer ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    <>
                      <i className="bi bi-person-fill"></i> Consumidor Final
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          <div className="col-md-3 mb-3">
            <label className="form-label">
              {t("document_type", { defaultValue: "Tipo" })} *
            </label>
            <Select
              options={invoiceTypeOptions}
              value={
                invoiceTypeOptions.find(
                  (o) => o.value === form.document_type_id,
                ) || null
              }
              onChange={(opt) => onChangeType(opt?.value)}
              isClearable={false}
            />
          </div>

          <div className="col-md-3 mb-3">
            <label className="form-label d-flex align-items-center gap-2">
              <span>{t("document_set", { defaultValue: "Série" })} *</span>
              {atRefreshing && (
                <small
                  className="text-muted d-flex align-items-center gap-1"
                  style={{ fontSize: 11 }}
                >
                  <span
                    className="spinner-border spinner-border-sm"
                    style={{ width: 10, height: 10 }}
                  />
                  {t("checking_at", { defaultValue: "a verificar AT..." })}
                </small>
              )}
            </label>
            <Select
              options={availableSetsForType.map((s) => {
                let suffix = "";
                if (s.at_communicated === true) suffix = " ✓ AT";
                else if (s.at_communicated === false) suffix = " (sem AT)";
                return {
                  value: s.document_set_id,
                  label: `${s.name}${suffix}`,
                  isDisabled: s.at_communicated === false,
                  raw: s,
                };
              })}
              value={
                availableSetsForType
                  .filter((s) => s.document_set_id === form.document_set_id)
                  .map((s) => {
                    let suffix = "";
                    if (s.at_communicated === true) suffix = " ✓ AT";
                    else if (s.at_communicated === false) suffix = " (sem AT)";
                    return { value: s.document_set_id, label: `${s.name}${suffix}` };
                  })[0] || null
              }
              onChange={(opt) =>
                setForm((p) => ({ ...p, document_set_id: opt?.value || null }))
              }
              placeholder={t("select_series", { defaultValue: "Seleccionar série..." })}
              isClearable
              noOptionsMessage={() =>
                "Sem séries disponíveis para este tipo nesta empresa Moloni"
              }
            />
          </div>

          <div className="col-md-3 mb-3">
            <label className="form-label">
              {t("date", { defaultValue: "Data" })}
            </label>
            <input
              type="date"
              className="form-control"
              value={form.date}
              onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
            />
          </div>

          <div className="col-md-3 mb-3">
            <label className="form-label">
              {t("expiration_date", { defaultValue: "Validade" })}
            </label>
            <input
              type="date"
              className="form-control"
              value={form.expiration_date}
              onChange={(e) =>
                setForm((p) => ({ ...p, expiration_date: e.target.value }))
              }
            />
          </div>

          <div className="col-md-3 mb-3">
            <label className="form-label">
              {t("payment_method", { defaultValue: "Método Pagamento" })}
            </label>
            <Select
              options={(options.payment_methods || []).map((p) => ({
                value: p.payment_method_id,
                label: p.name,
              }))}
              value={
                (options.payment_methods || [])
                  .filter((p) => p.payment_method_id === form.payment_method_id)
                  .map((p) => ({ value: p.payment_method_id, label: p.name }))[0] || null
              }
              onChange={(opt) =>
                setForm((p) => ({ ...p, payment_method_id: opt?.value || null }))
              }
              isClearable
              placeholder="—"
            />
          </div>

          <div className="col-md-3 mb-3">
            <label className="form-label">
              {t("maturity_date", { defaultValue: "Prazo Pagamento" })}
            </label>
            <Select
              options={(options.maturity_dates || []).map((m) => ({
                value: m.maturity_date_id,
                label: `${m.name}${m.days ? ` (${m.days}d)` : ""}`,
              }))}
              value={
                (options.maturity_dates || [])
                  .filter((m) => m.maturity_date_id === form.maturity_date_id)
                  .map((m) => ({
                    value: m.maturity_date_id,
                    label: `${m.name}${m.days ? ` (${m.days}d)` : ""}`,
                  }))[0] || null
              }
              onChange={(opt) =>
                setForm((p) => ({ ...p, maturity_date_id: opt?.value || null }))
              }
              isClearable
              placeholder="—"
            />
          </div>

          <div className="col-md-12 mb-3">
            <button
              type="button"
              className="btn btn-link p-0"
              onClick={() => setShowOptionalFields((v) => !v)}
              style={{ fontSize: 13 }}
            >
              <i
                className={`bi ${
                  showOptionalFields ? "bi-chevron-down" : "bi-chevron-right"
                }`}
              ></i>{" "}
              Campos opcionais (referências e notas)
              {(form.our_reference || form.your_reference || form.notes) && (
                <span
                  className="badge bg-secondary ms-2"
                  style={{ fontSize: 10 }}
                >
                  preenchidos
                </span>
              )}
            </button>
            {showOptionalFields && (
              <div
                className="row"
                style={{
                  border: "1px solid #dee2e6",
                  borderRadius: 6,
                  padding: 12,
                  marginTop: 6,
                  marginLeft: 0,
                  marginRight: 0,
                  background: "#f8f9fa",
                }}
              >
                <div className="col-md-6 mb-2">
                  <label className="form-label" style={{ fontSize: 13 }}>
                    A Nossa Referência
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={form.our_reference}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, our_reference: e.target.value }))
                    }
                    placeholder="Opcional"
                  />
                </div>
                <div className="col-md-6 mb-2">
                  <label className="form-label" style={{ fontSize: 13 }}>
                    A Sua Referência
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={form.your_reference}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, your_reference: e.target.value }))
                    }
                    placeholder="Opcional"
                  />
                </div>
                <div className="col-md-12">
                  <label className="form-label" style={{ fontSize: 13 }}>
                    Notas
                  </label>
                  <textarea
                    className="form-control form-control-sm"
                    rows="2"
                    value={form.notes}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, notes: e.target.value }))
                    }
                    placeholder="Opcional — aparecem no documento Moloni"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <h5>{t("lines", { defaultValue: "Linhas" })}</h5>
        {lines.some((l) => Number(l.qty) > 1) && (
          <div
            style={{
              backgroundColor: "#fff8e1",
              border: "1px solid #ffe69c",
              color: "#664d03",
              padding: "6px 10px",
              borderRadius: 4,
              marginBottom: 8,
              fontSize: 12,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
            title="Em produtos sincronizados de inventário TP, cada produto Moloni = 1 unidade. Se usares qty>1 numa linha, a baixa de stock será aleatória entre unidades. Recomenda-se 1 linha por unidade física."
          >
            <i
              className="bi bi-exclamation-triangle-fill"
              style={{ flexShrink: 0 }}
            ></i>
            <span>
              Linha com <strong>qty {">"} 1</strong> detectada — em peças TP
              sincronizadas, cada produto Moloni representa 1 unidade. Para
              baixa de stock determinística, usa o botão "TP" (sincroniza N
              unidades em N linhas).
            </span>
          </div>
        )}
        <table className="table align-middle">
          <thead>
            <tr>
              <th>Produto *</th>
              <th style={{ width: "8%" }}>Qty *</th>
              <th style={{ width: "12%" }}>Preço (s/IVA) *</th>
              <th style={{ width: "20%" }}>Imposto IVA *</th>
              <th style={{ width: "12%" }}>Subtotal s/IVA</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, idx) => {
              const lineSubtotal = Number(line.qty || 0) * Number(line.price || 0);
              const ivaTax = (companyTaxes.iva || []).find(
                (t) => Number(t.tax_id) === Number(line.iva_tax_id),
              );
              const lineIva = ivaTax ? lineSubtotal * (Number(ivaTax.value) / 100) : 0;
              const isExempt = !!line.exemption_reason;
              return (
                <React.Fragment key={idx}>
                  <tr>
                    <td>
                      {line.product_id ? (
                        <div
                          style={{
                            border: "1px solid #ced4da",
                            borderRadius: 6,
                            padding: "8px 10px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontWeight: 600,
                                fontSize: 14,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              title={line.name}
                            >
                              {line.name || "—"}
                            </div>
                            <small className="text-muted">
                              ID Moloni {line.product_id}
                            </small>
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => openProductModal(idx)}
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            style={{ flex: 1 }}
                            onClick={() => openProductModal(idx)}
                          >
                            <i className="bi bi-search"></i> Procurar produto
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-success"
                            onClick={() => openMoloniProductModal(idx)}
                            title="Criar produto Moloni"
                          >
                            <i className="bi bi-plus-lg"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-info"
                            onClick={() => openTpSyncModal(idx)}
                            title="Sincronizar peça TP"
                          >
                            <i className="bi bi-arrow-repeat"></i>
                          </button>
                        </div>
                      )}
                    </td>
                    <td>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        min="1"
                        step="1"
                        value={line.qty}
                        onChange={(e) => updateLine(idx, { qty: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        step="0.01"
                        min="0"
                        value={line.price}
                        onChange={(e) => updateLine(idx, { price: e.target.value })}
                      />
                    </td>
                    <td>
                      <Select
                        options={[
                          ...((companyTaxes.iva || []).map((t) => ({
                            value: `iva_${t.tax_id}`,
                            label: `${t.name} — ${t.value}%${t.is_default ? " (default)" : ""}`,
                            kind: "iva",
                            tax_id: t.tax_id,
                          }))),
                          {
                            value: "__exempt__",
                            label: "🚫 Isentar IVA (escolher motivo)",
                            kind: "exempt",
                          },
                        ]}
                        value={
                          line.iva_tax_id
                            ? (() => {
                                const t = (companyTaxes.iva || []).find(
                                  (x) => Number(x.tax_id) === Number(line.iva_tax_id),
                                );
                                return t
                                  ? { value: `iva_${t.tax_id}`, label: `${t.name} — ${t.value}%` }
                                  : null;
                              })()
                            : isExempt
                            ? {
                                value: "__exempt__",
                                label: `Isento — ${line.exemption_reason}`,
                              }
                            : null
                        }
                        onChange={(opt) => {
                          if (!opt) {
                            updateLine(idx, { iva_tax_id: null, exemption_reason: null });
                          } else if (opt.kind === "iva") {
                            updateLine(idx, {
                              iva_tax_id: Number(opt.tax_id),
                              exemption_reason: null,
                            });
                          } else if (opt.kind === "exempt") {
                            openExemptionModal(idx);
                          }
                        }}
                        placeholder="Escolher IVA / isenção"
                      />
                      {isExempt && (
                        <small
                          className="text-warning"
                          style={{ cursor: "pointer" }}
                          onClick={() => openExemptionModal(idx)}
                          title="Clica para alterar o motivo"
                        >
                          {(() => {
                            const r = exemptionReasons.find(
                              (e) => e.code === line.exemption_reason,
                            );
                            return r
                              ? `${r.code}: ${r.description}`
                              : line.exemption_reason;
                          })()}
                        </small>
                      )}
                    </td>
                    <td>€{lineSubtotal.toFixed(2)}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeLine(idx)}
                        disabled={lines.length === 1}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>

                  {/* Linha auxiliar — taxas extra (Ecotaxa, IS, etc.) */}
                  <tr key={`extras-${idx}`}>
                    <td colSpan={6} style={{ borderTop: 0, paddingTop: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 8,
                          fontSize: 12,
                          color: "#6c757d",
                          marginLeft: 8,
                        }}
                      >
                        {ivaTax && !isExempt && (
                          <span>IVA aplicável: <strong>€{lineIva.toFixed(2)}</strong></span>
                        )}
                        {(line.extra_taxes || []).length > 0 && (
                          <>
                            <span>•</span>
                            {(line.extra_taxes || []).map((et) => {
                              const tx = (companyTaxes.other || []).find(
                                (x) => Number(x.tax_id) === Number(et.tax_id),
                              );
                              if (!tx) return null;
                              const isPct = Number(tx.type) === 1;
                              return (
                                <span
                                  key={et.tax_id}
                                  style={{
                                    background: "#fff3cd",
                                    border: "1px solid #ffe69c",
                                    color: "#664d03",
                                    padding: "2px 8px",
                                    borderRadius: 12,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 4,
                                  }}
                                >
                                  {tx.name}
                                  {tx.value
                                    ? ` (${tx.value}${isPct ? "%" : "€"})`
                                    : ""}
                                  <button
                                    type="button"
                                    style={{
                                      border: 0,
                                      background: "transparent",
                                      color: "#664d03",
                                      padding: 0,
                                      lineHeight: 1,
                                      cursor: "pointer",
                                    }}
                                    onClick={() =>
                                      updateLine(idx, {
                                        extra_taxes: (line.extra_taxes || []).filter(
                                          (e) => Number(e.tax_id) !== Number(et.tax_id),
                                        ),
                                      })
                                    }
                                    title="Remover"
                                  >
                                    ×
                                  </button>
                                </span>
                              );
                            })}
                          </>
                        )}
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => openExtraTaxModal(idx)}
                          style={{ fontSize: 11, padding: "2px 8px" }}
                        >
                          <i className="bi bi-plus-lg"></i> Ecotaxa / outra taxa
                        </button>
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={6} style={{ textAlign: "right" }}>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={addLine}
                >
                  <i className="bi bi-plus-lg"></i> Adicionar linha
                </button>
              </td>
            </tr>
          </tfoot>
        </table>

        {/* Resumo + breakdown de impostos */}
        <div
          className="row"
          style={{ marginTop: 10, marginBottom: 15 }}
        >
          <div className="col-md-7">
            <div
              style={{
                border: "1px solid #dee2e6",
                borderRadius: 6,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: "8px 12px",
                  backgroundColor: "#f8f9fa",
                  borderBottom: "1px solid #dee2e6",
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                Impostos aplicados
              </div>
              {taxesBreakdown.groups.length === 0 ? (
                <div className="text-muted" style={{ padding: 12, fontSize: 13 }}>
                  Sem impostos aplicáveis ainda. Escolhe IVA ou isenção em cada
                  linha.
                </div>
              ) : (
                <table
                  className="table table-sm mb-0"
                  style={{ fontSize: 13 }}
                >
                  <thead style={{ backgroundColor: "#f8f9fa" }}>
                    <tr>
                      <th>Imposto</th>
                      <th style={{ textAlign: "right" }}>Base aplicável</th>
                      <th style={{ textAlign: "right" }}>Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {taxesBreakdown.groups.map((g, i) => (
                      <tr key={i}>
                        <td>
                          {g.kind === "exempt" ? (
                            <span style={{ color: "#856404" }}>{g.label}</span>
                          ) : g.kind === "extra" ? (
                            <span style={{ color: "#6c757d" }}>{g.label}</span>
                          ) : (
                            <strong>{g.label}</strong>
                          )}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          €{g.base.toFixed(2)}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          €{g.vat.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="col-md-5">
            <div
              style={{
                border: "1px solid #dee2e6",
                borderRadius: 6,
                padding: 12,
                backgroundColor: "#f8f9fa",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 14,
                }}
              >
                <span>Total sem IVA</span>
                <strong>€{taxesBreakdown.baseTotal.toFixed(2)}</strong>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 14,
                  marginTop: 6,
                }}
              >
                <span>Total IVA</span>
                <strong>€{taxesBreakdown.vatTotal.toFixed(2)}</strong>
              </div>
              {taxesBreakdown.extrasTotal > 0 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 13,
                    marginTop: 6,
                    color: "#6c757d",
                  }}
                >
                  <span>Outras taxas</span>
                  <strong>€{taxesBreakdown.extrasTotal.toFixed(2)}</strong>
                </div>
              )}
              <hr style={{ margin: "10px 0" }} />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 18,
                  fontWeight: 700,
                }}
              >
                <span>Total</span>
                <span>€{taxesBreakdown.grandTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            backgroundColor: "#eef7fb",
            border: "1px solid #bee5eb",
            color: "#0c5460",
            padding: 10,
            borderRadius: 4,
            marginBottom: 15,
            fontSize: 13,
          }}
        >
          <strong>
            {t("emission_notice_title", {
              defaultValue: "Rascunho vs. Final",
            })}
          </strong>
          {" — "}
          {t("emission_notice_body", {
            defaultValue:
              "Rascunho fica editável/apagável no portal Moloni e NÃO dá baixa de stock no TP — só a emissão final (ou conversão de rascunho em final no TP) actualiza o inventário.",
          })}
        </div>

        <div className="d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate("/faturacao")}
            disabled={submitting}
          >
            {t("cancel", { defaultValue: "Cancelar" })}
          </button>
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => submit("draft")}
            disabled={submitting}
          >
            {submitting ? (
              <span className="spinner-border spinner-border-sm" role="status" />
            ) : (
              <>
                <i className="bi bi-save"></i>{" "}
                {t("save_draft", { defaultValue: "Guardar rascunho" })}
              </>
            )}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => submit("final")}
            disabled={submitting}
          >
            {submitting ? (
              <span className="spinner-border spinner-border-sm" role="status" />
            ) : (
              <>
                <i className="bi bi-send-check"></i>{" "}
                {t("emit_final", { defaultValue: "Emitir final" })}
              </>
            )}
          </button>
        </div>
      </section>

      <Modal
        isOpen={moloniProductModal.open}
        toggle={closeMoloniProductModal}
        size="lg"
      >
        <ModalHeader toggle={closeMoloniProductModal}>
          {t("create_moloni_product", {
            defaultValue: "Criar produto na Moloni",
          })}
        </ModalHeader>
        <ModalBody>
          <div className="row">
            <div className="col-md-8 mb-3">
              <label className="form-label">
                {t("name", { defaultValue: "Nome" })} *
              </label>
              <input
                type="text"
                className="form-control"
                value={moloniProductForm.name}
                onChange={(e) =>
                  setMoloniProductForm((p) => ({ ...p, name: e.target.value }))
                }
              />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">
                {t("reference", { defaultValue: "Referência" })} *
              </label>
              <input
                type="text"
                className="form-control"
                value={moloniProductForm.reference}
                onChange={(e) =>
                  setMoloniProductForm((p) => ({
                    ...p,
                    reference: e.target.value,
                  }))
                }
              />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">
                {t("price_excl_vat", { defaultValue: "Preço (sem IVA)" })} *
              </label>
              <input
                type="number"
                className="form-control"
                step="0.01"
                min="0"
                value={moloniProductForm.price}
                onChange={(e) =>
                  setMoloniProductForm((p) => ({ ...p, price: e.target.value }))
                }
              />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">
                {t("category", { defaultValue: "Categoria" })} *
              </label>
              <Select
                options={(moloniProductOptions?.categories || []).map((c) => ({
                  value: c.category_id,
                  label: c.name,
                }))}
                value={
                  (moloniProductOptions?.categories || [])
                    .filter((c) => c.category_id === moloniProductForm.category_id)
                    .map((c) => ({ value: c.category_id, label: c.name }))[0] ||
                  null
                }
                onChange={(opt) =>
                  setMoloniProductForm((p) => ({
                    ...p,
                    category_id: opt?.value || null,
                  }))
                }
                placeholder="—"
              />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">
                {t("unit", { defaultValue: "Unidade" })} *
              </label>
              <Select
                options={(moloniProductOptions?.units || []).map((u) => ({
                  value: u.unit_id,
                  label: `${u.name} (${u.short_name})`,
                }))}
                value={
                  (moloniProductOptions?.units || [])
                    .filter((u) => u.unit_id === moloniProductForm.unit_id)
                    .map((u) => ({
                      value: u.unit_id,
                      label: `${u.name} (${u.short_name})`,
                    }))[0] || null
                }
                onChange={(opt) =>
                  setMoloniProductForm((p) => ({
                    ...p,
                    unit_id: opt?.value || null,
                  }))
                }
                placeholder="—"
              />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">Tipo</label>
              <Select
                options={[
                  { value: 1, label: t("product", { defaultValue: "Produto" }) },
                  { value: 2, label: t("service", { defaultValue: "Serviço" }) },
                ]}
                value={{
                  value: moloniProductForm.type,
                  label:
                    moloniProductForm.type === 2
                      ? t("service", { defaultValue: "Serviço" })
                      : t("product", { defaultValue: "Produto" }),
                }}
                onChange={(opt) =>
                  setMoloniProductForm((p) => ({ ...p, type: opt.value }))
                }
              />
            </div>
            <div className="col-md-8 mb-3">
              <label className="form-label">
                IVA aplicável *
              </label>
              {(() => {
                // Filtrar apenas IVA real: saft_type=1 + vat_type IVA + value>0
                const ivaTaxes = (moloniProductOptions?.taxes || []).filter(
                  (tx) =>
                    Number(tx.saft_type ?? tx.safe_type) === 1 &&
                    ["NOR", "INT", "RED"].includes(
                      String(tx.vat_type || "").toUpperCase(),
                    ) &&
                    Number(tx.value) > 0,
                );
                const opts = [
                  ...ivaTaxes.map((tx) => ({
                    value: `iva_${tx.tax_id}`,
                    label: `${tx.name} — ${tx.value}%${
                      tx.fiscal_zone ? ` · ${tx.fiscal_zone}` : ""
                    }`,
                    kind: "iva",
                    raw: tx,
                  })),
                  {
                    value: "__exempt__",
                    label: "🚫 Isentar IVA (escolher motivo)",
                    kind: "exempt",
                  },
                ];
                const currentValue = moloniProductForm.taxes[0]
                  ? opts.find(
                      (o) =>
                        o.kind === "iva" &&
                        Number(o.raw.tax_id) ===
                          Number(moloniProductForm.taxes[0].tax_id),
                    ) || null
                  : moloniProductForm.exemption_reason
                  ? {
                      value: "__exempt__",
                      label: `Isento — ${moloniProductForm.exemption_reason}`,
                      kind: "exempt",
                    }
                  : null;
                return (
                  <>
                    <Select
                      options={opts}
                      value={currentValue}
                      onChange={(opt) => {
                        if (!opt) {
                          setMoloniProductForm((p) => ({
                            ...p,
                            taxes: [],
                            exemption_reason: null,
                          }));
                        } else if (opt.kind === "iva") {
                          setMoloniProductForm((p) => ({
                            ...p,
                            taxes: [
                              {
                                tax_id: opt.raw.tax_id,
                                value: Number(opt.raw.value),
                              },
                            ],
                            exemption_reason: null,
                          }));
                        } else if (opt.kind === "exempt") {
                          // abre prompt simples — escolher M01-M99
                          const code = window.prompt(
                            "Motivo de isenção (M01-M99):",
                            moloniProductForm.exemption_reason || "M99",
                          );
                          if (!code) return;
                          const valid = exemptionReasons.find(
                            (e) => e.code === code.trim(),
                          );
                          if (!valid) {
                            toast.error(
                              "Motivo inválido. Usa um código M01-M99.",
                            );
                            return;
                          }
                          setMoloniProductForm((p) => ({
                            ...p,
                            taxes: [],
                            exemption_reason: valid.code,
                          }));
                        }
                      }}
                      isClearable
                      placeholder="Escolher IVA ou isentar"
                    />
                    {moloniProductForm.exemption_reason && (
                      <small className="text-warning">
                        Motivo: {moloniProductForm.exemption_reason}
                        {(() => {
                          const r = exemptionReasons.find(
                            (e) =>
                              e.code === moloniProductForm.exemption_reason,
                          );
                          return r ? ` — ${r.description}` : "";
                        })()}
                      </small>
                    )}
                    {ivaTaxes.length === 0 && (
                      <small className="text-danger d-block mt-1">
                        Sem taxas IVA reais (saft_type=1, NOR/INT/RED) na
                        empresa. Verifica em portal Moloni.
                      </small>
                    )}
                  </>
                );
              })()}
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">
                {t("summary", { defaultValue: "Resumo" })}
              </label>
              <input
                type="text"
                className="form-control"
                value={moloniProductForm.summary}
                onChange={(e) =>
                  setMoloniProductForm((p) => ({
                    ...p,
                    summary: e.target.value,
                  }))
                }
              />
            </div>
          </div>
          <div
            style={{
              backgroundColor: "#eef7fb",
              border: "1px solid #bee5eb",
              color: "#0c5460",
              padding: 10,
              borderRadius: 4,
              fontSize: 12,
            }}
          >
            {t("moloni_product_note", {
              defaultValue:
                "O produto fica criado na Moloni e pode ser reutilizado em futuras facturas.",
            })}
          </div>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={closeMoloniProductModal}
            disabled={moloniProductSaving}
          >
            {t("cancel", { defaultValue: "Cancelar" })}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={saveMoloniProduct}
            disabled={moloniProductSaving}
          >
            {moloniProductSaving ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              t("create", { defaultValue: "Criar" })
            )}
          </button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={tpSyncModal.open} toggle={closeTpSyncModal} size="lg">
        <ModalHeader toggle={closeTpSyncModal}>
          {t("use_tp_part", {
            defaultValue: "Usar peça TP (sincroniza para Moloni)",
          })}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <label className="form-label">
              {t("search_part", {
                defaultValue: "Pesquisar peça",
              })}
            </label>
            <input
              type="text"
              className="form-control"
              value={tpSearch}
              onChange={(e) => setTpSearch(e.target.value)}
              placeholder={t("search_part_placeholder", {
                defaultValue:
                  "Nome peça, ID TP (interno), referência, código de barras, marca ou modelo viatura",
              })}
              autoFocus
            />
            <small className="text-muted">
              {t("search_hint", {
                defaultValue:
                  "Pesquisa em: nome da peça, ID TP interno, ref. peça (part_code), descrição, código de barras, marca/modelo da viatura. Mínimo 2 caracteres.",
              })}
              {tpSearching && (
                <>
                  {" · "}
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                  />
                </>
              )}
            </small>
          </div>

          {tpResults.length > 0 && (
            <div
              style={{
                border: "1px solid #dee2e6",
                borderRadius: 4,
                maxHeight: 260,
                overflowY: "auto",
                marginBottom: 10,
              }}
            >
              {tpResults.map((p) => {
                const selected = tpSelectedPart?.id === p.id;
                return (
                  <div
                    key={p.id}
                    onClick={() => {
                      setTpSelectedPart(p);
                      setTpPriceOverride(String(p.price));
                    }}
                    style={{
                      padding: 8,
                      cursor: "pointer",
                      borderBottom: "1px solid #eee",
                      backgroundColor: selected ? "#e7f3ff" : "transparent",
                    }}
                  >
                    <div style={{ fontWeight: 500 }}>
                      <span
                        style={{
                          background: "#e7f3ff",
                          color: "#0d6efd",
                          padding: "1px 6px",
                          borderRadius: 3,
                          fontSize: 11,
                          marginRight: 6,
                        }}
                      >
                        ID TP {p.parts_internal_id}
                      </span>
                      {p.part_name || "—"}
                      {p.reference && (
                        <small className="text-muted ms-2">
                          Ref: <strong>{p.reference}</strong>
                        </small>
                      )}
                      {p.created_at && (
                        <small className="text-muted ms-2">
                          <i className="bi bi-calendar-event"></i> {p.created_at}
                        </small>
                      )}
                    </div>
                    <small className="text-muted">
                      {[p.make, p.model, p.year].filter(Boolean).join(" · ")}{" "}
                      · qty {p.quantity} · €{p.price.toFixed(2)}
                      {p.vat_included && " (c/IVA)"}
                    </small>
                  </div>
                );
              })}
            </div>
          )}

          {tpSearch.length >= 2 && !tpSearching && tpResults.length === 0 && (
            <div className="text-muted mb-3" style={{ fontSize: 13 }}>
              {t("no_parts_found", { defaultValue: "Sem peças encontradas." })}
            </div>
          )}

          {tpSelectedPart && (
            <div
              style={{
                backgroundColor: "#f8f9fa",
                border: "1px solid #dee2e6",
                padding: 12,
                borderRadius: 4,
              }}
            >
              <div style={{ marginBottom: 8 }}>
                <strong>
                  {tpSelectedPart.parts_internal_id} ·{" "}
                  {tpSelectedPart.part_name}
                </strong>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  {[
                    tpSelectedPart.make,
                    tpSelectedPart.model,
                    tpSelectedPart.year,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <label className="form-label">
                    Preço a sincronizar (sem IVA)
                  </label>
                  <div
                    style={{
                      display: "flex",
                      gap: 4,
                      marginBottom: 6,
                    }}
                  >
                    <button
                      type="button"
                      className={`btn btn-sm ${
                        Number(tpPriceOverride) === Number(tpSelectedPart.price)
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() =>
                        setTpPriceOverride(String(tpSelectedPart.price || 0))
                      }
                      style={{ flex: 1 }}
                    >
                      Preço 1<br />
                      <small>€{Number(tpSelectedPart.price || 0).toFixed(2)}</small>
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${
                        Number(tpPriceOverride) === Number(tpSelectedPart.original_price)
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() =>
                        setTpPriceOverride(
                          String(tpSelectedPart.original_price || 0),
                        )
                      }
                      disabled={!tpSelectedPart.original_price}
                      style={{ flex: 1 }}
                    >
                      Preço 2<br />
                      <small>
                        €{Number(tpSelectedPart.original_price || 0).toFixed(2)}
                      </small>
                    </button>
                  </div>
                  <input
                    type="number"
                    className="form-control"
                    step="0.01"
                    min="0"
                    value={tpPriceOverride}
                    onChange={(e) => setTpPriceOverride(e.target.value)}
                    placeholder="Ou escreve um valor manual"
                  />
                  <small className="text-muted">
                    Override temporário — não altera o preço da peça no
                    inventário TP.
                  </small>
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Quantidade a adicionar à factura
                  </label>
                  <div
                    style={{ display: "flex", gap: 4, alignItems: "center" }}
                  >
                    <button
                      type="button"
                      className={`btn btn-sm ${
                        Number(tpQty) === 1 ? "btn-primary" : "btn-outline-primary"
                      }`}
                      onClick={() => setTpQty(1)}
                    >
                      1
                    </button>
                    {tpSelectedPart.quantity >= 2 && (
                      <button
                        type="button"
                        className={`btn btn-sm ${
                          Number(tpQty) === 2
                            ? "btn-primary"
                            : "btn-outline-primary"
                        }`}
                        onClick={() => setTpQty(2)}
                      >
                        2
                      </button>
                    )}
                    {tpSelectedPart.quantity >= 3 && (
                      <button
                        type="button"
                        className={`btn btn-sm ${
                          Number(tpQty) === Number(tpSelectedPart.quantity)
                            ? "btn-primary"
                            : "btn-outline-primary"
                        }`}
                        onClick={() => setTpQty(Number(tpSelectedPart.quantity))}
                      >
                        Todas ({tpSelectedPart.quantity})
                      </button>
                    )}
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      style={{ maxWidth: 80 }}
                      min={1}
                      max={tpSelectedPart.quantity}
                      value={tpQty}
                      onChange={(e) => {
                        const n = Math.max(
                          1,
                          Math.min(
                            Number(tpSelectedPart.quantity),
                            Number(e.target.value) || 1,
                          ),
                        );
                        setTpQty(n);
                      }}
                    />
                  </div>
                  <small className="text-muted">
                    Disponível em inventário: {tpSelectedPart.quantity}.
                    {tpQty > 1 && ` Vão ser adicionadas ${tpQty} linhas.`}
                  </small>
                </div>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={closeTpSyncModal}
            disabled={tpSyncing}
          >
            {t("cancel", { defaultValue: "Cancelar" })}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={runTpSync}
            disabled={!tpSelectedPart || tpSyncing}
          >
            {tpSyncing ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              <>
                <i className="bi bi-arrow-repeat"></i>{" "}
                {t("sync_and_use", {
                  defaultValue: "Sincronizar e usar",
                })}
              </>
            )}
          </button>
        </ModalFooter>
      </Modal>
      {/* ==================== MODAL: Escolha de cliente ==================== */}
      <Modal isOpen={customerModal} toggle={closeCustomerModal} size="lg">
        <ModalHeader toggle={closeCustomerModal}>
          Cliente da factura
        </ModalHeader>
        <ModalBody>
          <div
            style={{
              display: "flex",
              gap: 8,
              marginBottom: 8,
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              className="btn btn-outline-dark"
              onClick={async () => {
                await pickFinalConsumer();
                closeCustomerModal();
              }}
              disabled={loadingFinalConsumer}
              title="NIF 999999990 — Consumidor Final / sem identificação"
            >
              <i className="bi bi-person-fill"></i> Consumidor Final
            </button>
            <button
              type="button"
              className="btn btn-outline-success"
              onClick={openCreateCustomerModal}
              title="Criar novo cliente na Moloni"
            >
              <i className="bi bi-person-plus"></i> Novo cliente
            </button>
          </div>
          <hr style={{ margin: "12px 0" }} />
          <label
            className="form-label"
            style={{ fontSize: 13, fontWeight: 500 }}
          >
            <i className="bi bi-search"></i> Pesquisar cliente existente
          </label>
          <input
            type="text"
            className="form-control form-control-lg"
            placeholder="Nome, NIF, email..."
            value={customerModalSearch}
            onChange={(e) => setCustomerModalSearch(e.target.value)}
            autoFocus
          />

          {customerModalSearch.length < 2 && (
            <div className="text-muted" style={{ fontSize: 13, padding: 8 }}>
              Escreve pelo menos 2 caracteres para procurar.
            </div>
          )}

          {customerModalSearching && (
            <div style={{ padding: 8, color: "#6c757d", fontSize: 13 }}>
              <span className="spinner-border spinner-border-sm me-2" /> A
              procurar...
            </div>
          )}

          {!customerModalSearching && customerModalSearch.length >= 2 &&
            customerModalResults.length === 0 && (
              <div className="text-muted" style={{ padding: 12 }}>
                Sem resultados.
              </div>
            )}

          <div style={{ maxHeight: 480, overflowY: "auto" }}>
            {customerModalResults.map((c) => (
              <div
                key={c.customer_id}
                onClick={() => {
                  setForm((p) => ({ ...p, customer: c }));
                  applyCustomerDefaults(c);
                  closeCustomerModal();
                }}
                style={{
                  border: "1px solid #dee2e6",
                  borderRadius: 6,
                  padding: 12,
                  marginBottom: 8,
                  cursor: "pointer",
                  background: "#fff",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f7ff")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
              >
                <div style={{ fontWeight: 600, fontSize: 15 }}>{c.name || "—"}</div>
                <div style={{ color: "#6c757d", fontSize: 13, marginTop: 2 }}>
                  {c.vat && <>NIF {c.vat}</>}
                  {c.email && <> · {c.email}</>}
                  {c.phone && <> · {c.phone}</>}
                </div>
                {(c.address || c.city) && (
                  <div style={{ color: "#6c757d", fontSize: 12, marginTop: 2 }}>
                    {[c.address, c.zip_code, c.city].filter(Boolean).join(", ")}
                  </div>
                )}
              </div>
            ))}
          </div>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={closeCustomerModal}
          >
            Fechar
          </button>
        </ModalFooter>
      </Modal>

      {/* ==================== MODAL: Criar cliente Moloni ==================== */}
      <Modal isOpen={createCustomerModal} toggle={closeCreateCustomerModal} size="lg">
        <ModalHeader toggle={closeCreateCustomerModal}>
          Criar cliente na Moloni
        </ModalHeader>
        <ModalBody>
          <div className="row">
            <div className="col-md-8 mb-3">
              <label className="form-label">Nome *</label>
              <input
                type="text"
                className="form-control"
                value={createCustomerForm.name}
                onChange={(e) =>
                  setCreateCustomerForm((p) => ({ ...p, name: e.target.value }))
                }
                placeholder="Nome ou razão social"
                autoFocus
              />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">
                NIF / VAT *{" "}
                {vatLookup.checking && (
                  <span
                    className="spinner-border spinner-border-sm ms-1"
                    style={{ width: 12, height: 12 }}
                  />
                )}
              </label>
              <input
                type="text"
                className={`form-control ${
                  vatLookup.exists ? "is-invalid" : ""
                }`}
                value={createCustomerForm.vat}
                onChange={(e) =>
                  setCreateCustomerForm((p) => ({ ...p, vat: e.target.value }))
                }
                placeholder="Ex: 508040710"
              />
              {vatLookup.exists && vatLookup.customer && (
                <div
                  className="mt-2 p-2"
                  style={{
                    background: "#fff3cd",
                    border: "1px solid #ffe69c",
                    borderRadius: 4,
                    fontSize: 12,
                    color: "#664d03",
                  }}
                >
                  <strong>NIF já existe</strong> — "{vatLookup.customer.name}"
                  <button
                    type="button"
                    className="btn btn-sm btn-warning d-block mt-1"
                    onClick={useExistingCustomerFromLookup}
                  >
                    Usar este cliente
                  </button>
                </div>
              )}

              {/* VATSense (VIES) — só mostra se NIF >= 8 e já não existe localmente */}
              {!vatLookup.exists &&
                createCustomerForm.vat &&
                createCustomerForm.vat.length >= 8 && (
                  <div className="mt-2" style={{ fontSize: 12 }}>
                    {vatSense.fetching ? (
                      <span className="text-muted">
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          style={{ width: 10, height: 10 }}
                        />
                        A validar via VIES...
                      </span>
                    ) : vatSense.valid ? (
                      <div
                        className="p-2"
                        style={{
                          background: "#d1e7dd",
                          border: "1px solid #a3cfbb",
                          borderRadius: 4,
                          color: "#0a3622",
                        }}
                      >
                        <div>
                          <i className="bi bi-shield-check"></i>{" "}
                          <strong>VAT válido (VIES)</strong>
                        </div>
                        <div style={{ marginTop: 2 }}>{vatSense.name}</div>
                        {vatSense.address && (
                          <div style={{ color: "#366948", fontSize: 11 }}>
                            {vatSense.address}
                          </div>
                        )}
                        <button
                          type="button"
                          className="btn btn-sm btn-success d-block mt-1"
                          onClick={applyVatSenseToForm}
                        >
                          <i className="bi bi-magic"></i> Auto-preencher dados
                        </button>
                      </div>
                    ) : vatSense.fetched && vatSense.error ? (
                      <span className="text-muted">
                        <i className="bi bi-info-circle"></i>{" "}
                        {vatSense.error}
                      </span>
                    ) : null}
                  </div>
                )}
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-control"
                value={createCustomerForm.email}
                onChange={(e) =>
                  setCreateCustomerForm((p) => ({ ...p, email: e.target.value }))
                }
                placeholder="cliente@empresa.pt"
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Telefone</label>
              <input
                type="text"
                className="form-control"
                value={createCustomerForm.phone}
                onChange={(e) =>
                  setCreateCustomerForm((p) => ({ ...p, phone: e.target.value }))
                }
              />
            </div>

            <div className="col-md-12 mb-3">
              <label className="form-label">Morada</label>
              <input
                type="text"
                className="form-control"
                value={createCustomerForm.address}
                onChange={(e) =>
                  setCreateCustomerForm((p) => ({ ...p, address: e.target.value }))
                }
                placeholder="Rua, número, andar..."
              />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Código Postal</label>
              <input
                type="text"
                className="form-control"
                value={createCustomerForm.zip_code}
                onChange={(e) =>
                  setCreateCustomerForm((p) => ({
                    ...p,
                    zip_code: e.target.value,
                  }))
                }
                placeholder="0000-000"
              />
            </div>
            <div className="col-md-5 mb-3">
              <label className="form-label">Cidade</label>
              <input
                type="text"
                className="form-control"
                value={createCustomerForm.city}
                onChange={(e) =>
                  setCreateCustomerForm((p) => ({ ...p, city: e.target.value }))
                }
              />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">País</label>
              <Select
                options={[
                  { value: 1, label: "Portugal" },
                  { value: 67, label: "Espanha" },
                  { value: 73, label: "França" },
                  { value: 80, label: "Alemanha" },
                  { value: 105, label: "Itália" },
                  { value: 222, label: "Reino Unido" },
                  { value: 232, label: "Estados Unidos" },
                  { value: 27, label: "Brasil" },
                ]}
                value={(() => {
                  const m = {
                    1: "Portugal",
                    67: "Espanha",
                    73: "França",
                    80: "Alemanha",
                    105: "Itália",
                    222: "Reino Unido",
                    232: "Estados Unidos",
                    27: "Brasil",
                  };
                  const id = createCustomerForm.country_id;
                  return m[id]
                    ? { value: id, label: m[id] }
                    : { value: 1, label: "Portugal" };
                })()}
                onChange={(opt) =>
                  setCreateCustomerForm((p) => ({
                    ...p,
                    country_id: opt?.value || 1,
                  }))
                }
              />
            </div>

            <div className="col-md-12 mb-3">
              <label className="form-label">Notas internas (opcional)</label>
              <textarea
                className="form-control"
                rows="2"
                value={createCustomerForm.notes}
                onChange={(e) =>
                  setCreateCustomerForm((p) => ({ ...p, notes: e.target.value }))
                }
              />
            </div>
          </div>

          <div
            style={{
              fontSize: 12,
              color: "#6c757d",
              padding: 8,
              background: "#f8f9fa",
              borderRadius: 4,
            }}
          >
            <i className="bi bi-info-circle"></i> Antes de criar, o sistema
            verifica se já existe um cliente com este NIF. Se existir, vai
            usar o existente em vez de duplicar.
          </div>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={closeCreateCustomerModal}
            disabled={createCustomerSaving}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={submitCreateCustomer}
            disabled={createCustomerSaving || vatLookup.exists}
          >
            {createCustomerSaving ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              <>
                <i className="bi bi-check-lg"></i> Criar e usar
              </>
            )}
          </button>
        </ModalFooter>
      </Modal>

      {/* ==================== MODAL: Escolha de produto ==================== */}
      <Modal
        isOpen={productModal.open}
        toggle={closeProductModal}
        size="lg"
      >
        <ModalHeader toggle={closeProductModal}>
          Escolher produto
        </ModalHeader>
        <ModalBody>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <input
              type="text"
              className="form-control form-control-lg"
              placeholder="Filtra por nome ou referência..."
              value={productModalSearch}
              onChange={(e) => setProductModalSearch(e.target.value)}
              autoFocus
            />
            <button
              type="button"
              className="btn btn-outline-success"
              onClick={() => {
                const idx = productModal.lineIdx;
                closeProductModal();
                openMoloniProductModal(idx);
              }}
              title="Criar novo produto na Moloni"
            >
              <i className="bi bi-plus-lg"></i> Novo Moloni
            </button>
            <button
              type="button"
              className="btn btn-outline-info"
              onClick={() => {
                const idx = productModal.lineIdx;
                closeProductModal();
                openTpSyncModal(idx);
              }}
              title="Sincronizar peça TP para Moloni"
            >
              <i className="bi bi-arrow-repeat"></i> Sync TP
            </button>
          </div>

          {!productsLoaded && (
            <div style={{ color: "#6c757d", fontSize: 13 }}>
              <span className="spinner-border spinner-border-sm me-2" /> A
              carregar produtos...
            </div>
          )}

          {productsLoaded && (() => {
            const q = productModalSearch.toLowerCase().trim();
            const filtered = (products || []).filter((p) => {
              if (!q) return true;
              return (
                String(p.name || "").toLowerCase().includes(q) ||
                String(p.reference || "").toLowerCase().includes(q)
              );
            });
            if (filtered.length === 0) {
              return (
                <div className="text-muted" style={{ padding: 12 }}>
                  {q
                    ? "Sem resultados — tenta criar produto Moloni ou sync TP."
                    : "Sem produtos sincronizados. Cria um produto Moloni ou sincroniza peça TP."}
                </div>
              );
            }
            return (
              <div style={{ maxHeight: 480, overflowY: "auto" }}>
                {filtered.map((p) => (
                  <div
                    key={p.moloni_id}
                    onClick={() => {
                      const idx = productModal.lineIdx;
                      if (idx !== null && idx !== undefined) {
                        const currentLine = lines[idx] || {};
                        const update = {
                          product_id: p.moloni_id,
                          name: p.name,
                          price: Number(p.price) || 0,
                        };
                        // Auto-fill IVA se a linha não tiver ainda
                        if (!currentLine.iva_tax_id && !currentLine.exemption_reason) {
                          const resolved = resolveDefaultIvaForLine(p);
                          update.iva_tax_id = resolved.iva_tax_id;
                          update.exemption_reason = resolved.exemption_reason;
                        }
                        updateLine(idx, update);
                      }
                      closeProductModal();
                    }}
                    style={{
                      border: "1px solid #dee2e6",
                      borderRadius: 6,
                      padding: 12,
                      marginBottom: 8,
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 12,
                      background: "#fff",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f7ff")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>
                        {p.name || "—"}
                      </div>
                      <small className="text-muted">
                        Ref: {p.reference || "—"} · ID Moloni {p.moloni_id}
                      </small>
                    </div>
                    <div style={{ fontWeight: 600, fontSize: 15, whiteSpace: "nowrap" }}>
                      €{Number(p.price || 0).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={closeProductModal}
          >
            Fechar
          </button>
        </ModalFooter>
      </Modal>

      {/* ==================== MODAL: Taxas extra (Ecotaxa, IS, etc.) ==================== */}
      <Modal isOpen={extraTaxModal.open} toggle={closeExtraTaxModal}>
        <ModalHeader toggle={closeExtraTaxModal}>
          Adicionar taxa
        </ModalHeader>
        <ModalBody>
          <p style={{ fontSize: 13, color: "#6c757d" }}>
            Selecciona as taxas adicionais a aplicar nesta linha (Ecotaxa,
            Imposto Selo, etc.). IVA é gerido na coluna principal.
          </p>
          {(companyTaxes.other || []).length === 0 ? (
            <div className="text-muted">
              Sem taxas extra configuradas na Moloni.
            </div>
          ) : (
            <div>
              {(companyTaxes.other || []).map((tx) => {
                const idx = extraTaxModal.lineIdx;
                if (idx == null) return null;
                const line = lines[idx];
                const checked = (line?.extra_taxes || []).some(
                  (et) => Number(et.tax_id) === Number(tx.tax_id),
                );
                const isPct = Number(tx.type) === 1;
                return (
                  <label
                    key={tx.tax_id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: 10,
                      border: "1px solid #dee2e6",
                      borderRadius: 6,
                      marginBottom: 6,
                      cursor: "pointer",
                      background: checked ? "#fff3cd" : "#fff",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        const next = (line.extra_taxes || []).filter(
                          (et) => Number(et.tax_id) !== Number(tx.tax_id),
                        );
                        if (isChecked) {
                          next.push({
                            tax_id: Number(tx.tax_id),
                            value: Number(tx.value) || 0,
                          });
                        }
                        updateLine(idx, { extra_taxes: next });
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600 }}>{tx.name}</div>
                      <small className="text-muted">
                        {tx.value
                          ? `${tx.value}${isPct ? "% sobre o subtotal" : "€ por linha"}`
                          : "sem valor"}
                        {tx.fiscal_zone && ` · ${tx.fiscal_zone}`}
                      </small>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-primary"
            onClick={closeExtraTaxModal}
          >
            Concluir
          </button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={exemptionModal.open} toggle={closeExemptionModal} size="lg">
        <ModalHeader toggle={closeExemptionModal}>
          Motivo de isenção de IVA
        </ModalHeader>
        <ModalBody>
          <p style={{ fontSize: 13, color: "#6c757d" }}>
            Escolhe o motivo de isenção de IVA aplicável a esta linha. Códigos
            oficiais da Autoridade Tributária (M01-M99).
          </p>
          <div style={{ maxHeight: 400, overflowY: "auto" }}>
            <table className="table table-hover table-sm">
              <tbody>
                {exemptionReasons.map((r) => (
                  <tr
                    key={r.code}
                    onClick={() => applyExemption(r.code)}
                    style={{ cursor: "pointer" }}
                  >
                    <td style={{ width: 80 }}>
                      <strong>{r.code}</strong>
                    </td>
                    <td>{r.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={closeExemptionModal}
          >
            Cancelar
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default NovaFactura;
